import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Headline6 from '../Typography/Heading/Headline6';
import MenuIcon from '../Icons/Menu';
import breakpoints from '../Theme/breakpoints';
import colors from '../Theme/colors';
import gradients from '../Theme/gradients';

const NavigationBar = styled.nav`
  background-color: ${({ theme }) => theme.colors.white};
  height: 106px;
`;

const navigationBarRow = css`
  width: 100%;
  height: auto;
`;

const NavigationBarUpperRow = styled.div`
  ${navigationBarRow}
  border-bottom: 1px solid ${({ isExecutive } : { isExecutive?: boolean }) => (
    isExecutive ? colors.blackHigh : colors.greyLight)};
  background-color: ${({ isExecutive } : { isExecutive?: boolean }) => (
    isExecutive ? colors.blackHigh : colors.white)};
  color: ${({ isExecutive } : { isExecutive?: boolean }) => (
    isExecutive ? colors.white : colors.blackHigh)};
  path {
    fill: ${({ isExecutive } : { isExecutive?: boolean }) => (
    isExecutive ? colors.white : colors.blackHigh)};
  }
  .MuiAvatar-root path {
    fill: unset;
  }
`;

const NavigationBarLowerRow = styled.div`
  ${navigationBarRow}
  border-bottom: 1px solid ${({ theme }) => theme.colors.blackMedium};
`;

const navigationBarMenu = css`
  max-width: 1412px;
  width: calc(100% - 24px);
  margin: 0 auto;
  display: flex;
  align-items: center;
  padding: 0px 12px;

  /* stylelint-disable-next-line selector-type-no-unknown */
  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    overflow: hidden;
  }
`;

const NavigationUpperMenu = styled.div`
  ${navigationBarMenu}
  height: 64px;
  justify-content: space-between;
`;

const NavigationLowerMenu = styled.div`
  ${navigationBarMenu}
  height: 40px;
  overflow-x: scroll;
  justify-content: flex-start;

  @media (min-width: ${breakpoints.sm}px) {
    justify-content: center;
    overflow-x: auto;
  }
`;

const NavigationBarTitleSection = styled.div`
  display: flex;
  width: 100%;

  /* stylelint-disable-next-line selector-type-no-unknown */
  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    width: 38%;
  }
`;

const NavigationBarSection = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
`;

const NavigationLink = styled(Headline6)`
  && {
    font-size: 14px;
    text-transform: none;
    letter-spacing: -0.005rem;
    padding: 0 2vw 0 9px;
    white-space: nowrap;

    @media (min-width: ${breakpoints.sm}px) {
      padding: 0 calc(1vw + 4px);
    }

    @media (min-width: ${breakpoints.lg}px) {
      padding: 0 16px;
    }

    &.user-tab {
      padding: 0 8px;
      display: none;

      @media (min-width: ${breakpoints.md}px) {
        display: block;
      }
    }
  }
`;

const NavigationBarIcon = styled.span`
  display: flex;
  cursor: pointer;
  padding: 0 6px;
  ${({ collapsible } : { collapsible?: boolean }) => (
    collapsible ? 'display: none;' : '')}

  @media (min-width: ${breakpoints.xssm}px) {
    display: flex;
    align-self: center;
  }
`;

const NavigationBarTitle = styled.span`
  margin: 0 auto;
  align-self: center;

  @media (min-width: ${breakpoints.xssm}px) {
    padding-left: 72px;
  }

  @media (min-width: ${breakpoints.md}px) {
    padding-left: 182px;
  }
`;

const NavigationBarUserButtons = styled.div`
  button {
    color: ${({ executive } : { executive?: boolean }) => (
    executive ? colors.white : 'inherit')};
    display: none;
    @media (min-width: ${breakpoints.md}px) {
      display: block;
    }
  }
  a {
    width: auto;
  }

  @media (min-width: ${breakpoints.md}px) {
    min-width: 98px;
    margin-right: 12px;
    display: inline-flex;
    justify-content: flex-end;
  }
`;

export const MainNavigationTab = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  background-color: ${({ executive } : { executive?: boolean }) => (
    executive ? 'black' : 'inherit')};
  background-image: ${({ executive } : { executive?: boolean }) => (
    executive ? `linear-gradient(0.04turn, ${gradients.executive})` : 'none')};
  a, a span {
    ${(
    { executive } : { executive?: boolean },
  ) => executive && `color: ${colors.white};`
}
  }
`;

const navigationPropertyTypes = {
  onOpenDrawer: PropTypes.func.isRequired,
  topAppBarTitle: PropTypes.node,
  tabs: PropTypes.node.isRequired,
  userTabs: PropTypes.node.isRequired,
  userButtons: PropTypes.node,
  isExecutive: PropTypes.bool,
};

type NavigationProperties = InferProps<typeof navigationPropertyTypes>;

const Navigation = ({
  onOpenDrawer,
  topAppBarTitle,
  tabs,
  userTabs,
  userButtons,
  isExecutive,
}: NavigationProperties) => (
  <NavigationBar>
    <NavigationBarUpperRow isExecutive={isExecutive ?? false}>
      <NavigationUpperMenu>
        <NavigationBarTitleSection>
          <NavigationBarIcon
            onClick={onOpenDrawer}
            data-testid="toggle-drawer"
            aria-label="drawer menu"
          >
            <MenuIcon />
          </NavigationBarIcon>
          <NavigationBarTitle>
            {topAppBarTitle}
          </NavigationBarTitle>
        </NavigationBarTitleSection>
        <NavigationBarUserButtons
          executive={isExecutive ?? false}
        >
          {userButtons}
        </NavigationBarUserButtons>
        <NavigationBarSection>
          {userTabs}
        </NavigationBarSection>
      </NavigationUpperMenu>
    </NavigationBarUpperRow>
    <NavigationBarLowerRow>
      <NavigationLowerMenu>
        <NavigationBarSection>
          {tabs}
        </NavigationBarSection>
      </NavigationLowerMenu>
    </NavigationBarLowerRow>
  </NavigationBar>
);

Navigation.propTypes = navigationPropertyTypes;

export {
  NavigationBarIcon,
  NavigationLink,
};

export default Navigation;
